import { useEffect } from 'react'
import { trackEvent } from '../utils/mixpanel'

const TermsOfService = () => {
  useEffect(() => { trackEvent('Page Viewed', { page: 'Terms of Service' }) }, [])

  return (
    <div className="bg-white min-h-screen">
      <div className="container mx-auto px-4 py-16 max-w-3xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-gray-500 mb-10">Last updated: June 2, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
            <p>
              Welcome to Wellness Loop. By accessing or using the wellnessloop.in website and
              related services (collectively, the "Platform"), you agree to be bound by these
              Terms of Service ("Terms"). If you do not agree to these Terms, please do not use
              the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
            <p>
              Wellness Loop is a platform that connects wellness seekers with curated wellness
              experiences, events, classes, retreats, facilitators, and programs. We provide
              tools for event discovery, registration, community engagement, and wellness
              journey tracking.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. User Accounts</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You must be at least 18 years of age to create an account</li>
              <li>You must notify us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. Event Registration and Payments</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.1 Registration</h3>
            <p>
              When you register for an event, class, or program through the Platform, you enter
              into an arrangement with the respective facilitator or host. Wellness Loop
              facilitates this connection but is not the direct provider of the wellness
              experience unless explicitly stated.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.2 Payments</h3>
            <p>
              Payments for events and programs are processed through our third-party payment
              provider (Razorpay). By making a payment, you agree to the payment provider's
              terms and conditions. All prices are listed in Indian Rupees (INR) unless otherwise
              stated.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.3 Refunds and Cancellations</h3>
            <p>
              Refund and cancellation policies vary by event and facilitator. Please review the
              specific refund policy listed on the event page before registering. For programs
              and cohort-based experiences, cancellation terms will be communicated at the time
              of enrollment. If no specific policy is stated, please contact us at{' '}
              <a href="mailto:hello@wellnessloop.in" className="text-indigo-600 hover:text-indigo-800 underline">
                hello@wellnessloop.in
              </a>{' '}
              for assistance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. User Conduct</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Platform for any unlawful purpose</li>
              <li>Impersonate any person or entity, or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the Platform's functionality or servers</li>
              <li>Upload or transmit viruses, malware, or any harmful code</li>
              <li>Scrape, crawl, or use automated means to access the Platform without permission</li>
              <li>Harass, abuse, or harm other users, facilitators, or staff</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>Use the Platform to spam or send unsolicited communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Facilitator Terms</h2>
            <p>
              If you register as a facilitator on the Platform, you additionally agree to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide accurate information about your qualifications, experience, and services</li>
              <li>Deliver the wellness experiences as described in your event listings</li>
              <li>Respond to participant communications in a timely manner</li>
              <li>Comply with all applicable laws and hold necessary certifications or licenses</li>
              <li>Not engage in any misleading health claims or guarantee specific outcomes</li>
            </ul>
            <p className="mt-2">
              Wellness Loop reserves the right to remove facilitator listings that violate these
              terms or receive repeated complaints.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Intellectual Property</h2>
            <p>
              All content on the Platform, including text, graphics, logos, images, and software,
              is the property of Wellness Loop or its content providers and is protected by
              applicable intellectual property laws. You may not reproduce, distribute, or
              create derivative works from any content on the Platform without prior written
              consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. User Content</h2>
            <p>
              By submitting content to the Platform (including testimonials, feedback, reviews,
              and check-in responses), you grant Wellness Loop a non-exclusive, royalty-free,
              worldwide license to use, display, and distribute such content in connection with
              the Platform and its promotion.
            </p>
            <p>
              You represent that you own or have the necessary rights to submit such content
              and that it does not violate any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Health Disclaimer</h2>
            <p>
              The wellness experiences, content, and information provided through the Platform
              are for general wellness and informational purposes only. They are{' '}
              <strong>not a substitute for professional medical advice, diagnosis, or treatment</strong>.
              Always consult a qualified healthcare provider before starting any new wellness
              practice, especially if you have existing health conditions.
            </p>
            <p>
              Wellness Loop and its facilitators do not guarantee specific health outcomes.
              Participation in any wellness activity is at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Wellness Loop shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages arising out of
              or related to your use of the Platform, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Injuries or health issues arising from participation in wellness events</li>
              <li>Loss of data or unauthorized access to your account</li>
              <li>Service interruptions or technical failures</li>
              <li>Actions or omissions of facilitators or third-party service providers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Wellness Loop, its founders, employees,
              and partners from any claims, damages, losses, or expenses arising from your use
              of the Platform, violation of these Terms, or infringement of any third-party
              rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account and access to the
              Platform at any time, with or without notice, for conduct that we determine
              violates these Terms or is harmful to other users, facilitators, or the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. Modifications to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of significant
              changes by posting the updated Terms on the Platform with a revised "Last updated"
              date. Your continued use of the Platform after changes are posted constitutes your
              acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of
              India. Any disputes arising from these Terms or your use of the Platform shall be
              subject to the exclusive jurisdiction of the courts in Bangalore, Karnataka, India.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">15. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Wellness Loop</strong><br />
              Email:{' '}
              <a href="mailto:hello@wellnessloop.in" className="text-indigo-600 hover:text-indigo-800 underline">
                hello@wellnessloop.in
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService
